import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Form, Input, Radio, Button, Progress, Alert, Select } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, MessageOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { apiRequest } from '../../utils/api';
// Import the images
import bg1 from '../../assets/top_bg_1.png';
import bg2 from '../../assets/top_bg_2.png';
import bg3 from '../../assets/top_bg_3.jpg';

import Services from '../Services/Services';
import Reviews from '../Reviews/Reviews';

const Section = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [form] = Form.useForm();
  const [isSending, setIsSending] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ visible: false, type: 'success', message: '' });
  
  // Use the imported images
  const backgrounds = [bg1, bg2, bg3];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % backgrounds.length);
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  const scrollTo = (to) => {
    document.getElementById(to)?.scrollIntoView({ behavior: 'smooth' });
  };

  const onFinish = async (values) => {
    setIsSending(true);
    setAlertInfo({ visible: false, type: '', message: '' });

    try {
      await apiRequest('/send-email', {
        method: 'POST',
        body: JSON.stringify({
          type: 'quick_quote',
          templateParams: {
            name: values.name,
            email: values.email,
            phone: values.phone,
            service: values.service === 'other' ? values.otherService : values.service,
          },
        }),
      });

      form.resetFields();
      setAlertInfo({
        visible: true,
        type: 'success',
        message: 'Paldies! Mēs ar jums sazināsimies drīz.'
      });
    } catch (error) {
      setAlertInfo({
        visible: true,
        type: 'error',
        message: error.message || 'Kļūda nosūtot ziņojumu. Lūdzu, mēģiniet vēlreiz.'
      });
    } finally {
      setIsSending(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    // setAlertInfo({
    //   visible: true,
    //   type: 'warning',
    //   message: 'Lūdzu, aizpildiet visus obligātos laukus.'
    // });
  };

  // Hide alert when user starts typing again
  const handleFormChange = () => {
    if (alertInfo.visible) {
      setAlertInfo({ ...alertInfo, visible: false });
    }
  };

  const inputStyle = "bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:border-green-400 focus:ring-1 focus:ring-green-400 transition-all";

  return (
    <>
      <div className="relative h-[80vh] w-full overflow-hidden bg-gray-900">
        {/* Single Background Image */}
        <div
          className="absolute inset-0 w-full h-full bg-center bg-cover transition-opacity duration-1000"
          style={{
            backgroundImage: `url(${backgrounds[currentSlide]})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />

        {/* Dark overlay to ensure text visibility */}
        <div className="absolute inset-0 bg-black/30" />

        {/* Content Overlay */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4 md:px-72">
          {/* Main Heading */}
          <div className="leading-tight">
            <div className="font-bold text-6xl md:text-[12vh]">
              PALUTINI <br /> SAVUS
            </div>
            <div className="font-light text-5xl md:text-[12vh]">
              STIKLUS
            </div>
          </div>

          {/* Subheading */}
          <div className="font-light text-xl md:text-xl mt-4">
            AUGSTĀKĀS KVALITĀTES APLĪMĒŠANA VENTSPILĪ
          </div>

          {/* Contact Button */}
          <button
            onClick={() => scrollTo('closer')}
            className="mt-8 md:mt-12 px-8 py-2 border border-white tracking-widest 
                     hover:bg-white hover:text-black transition-colors duration-300"
          >
            KONTAKTI
          </button>

          {/* Carousel Dots */}
          <div className="flex gap-2 mt-8 md:mt-12">
            {backgrounds.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 
                  ${currentSlide === index ? 'bg-white w-4' : 'bg-white/50'}`}
              />
            ))}
          </div>
        </div>
      </div>

      <Services />

      {/* Līmē Pats! Shop Section */}
      <div className="w-full py-16 px-4 bg-oklch-dark text-white" style={{ backgroundColor: 'oklch(27.8% 0.033 256.848)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">LĪMĒ PATS!</h2>
            <p className="text-xl max-w-3xl mx-auto">
              Mūsu e-veikalā pieejamas augstas kvalitātes plēves pašrocīgai uzstādīšanai. 
              Izvēlieties sev piemērotāko no plašā klāsta!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Auto plēves */}
            <Link 
              to="/shop?category=automotive" 
              className="bg-gray-900 rounded-xl overflow-hidden shadow-xl transform transition-all duration-300 hover:scale-105"
            >
              <div className="p-8 text-center">
                <div className="bg-green-400 inline-block p-4 rounded-full mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-2">Auto Plēves</h3>
                <p className="text-gray-300 mb-4">
                  Augstas kvalitātes tonējamās plēves auto logiem ar dažādām gaismas caurlaidības pakāpēm. 
                  Izcila kvalitāte, vienkārša uzstādīšana.
                </p>
                <span className="inline-block px-6 py-3 bg-green-400 text-black font-medium rounded-md">
                  Apskatīt plēves
                </span>
              </div>
            </Link>

            {/* Mājokļu plēves */}
            <Link 
              to="/shop?category=residential" 
              className="bg-gray-900 rounded-xl overflow-hidden shadow-xl transform transition-all duration-300 hover:scale-105"
            >
              <div className="p-8 text-center">
                <div className="bg-green-400 inline-block p-4 rounded-full mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-2">Mājokļu Plēves</h3>
                <p className="text-gray-300 mb-4">
                  Spoguļplēves mājas un biroja logiem ar saules siltuma atstarošanas funkciju. 
                  Pieejamas dažādas intensitātes pakāpes.
                </p>
                <span className="inline-block px-6 py-3 bg-green-400 text-black font-medium rounded-md">
                  Apskatīt plēves
                </span>
              </div>
            </Link>
          </div>
        </div>
      </div>
      <div className="w-full py-8 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Fast Delivery */}
            <div className="flex flex-col items-center text-center text-white">
              <div className="bg-green-400 text-gray-900 p-3 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Ātra Piegāde</h3>
              <p className="text-gray-300">Piegāde visā Latvijā 1-3 darba dienu laikā. Ventspilī iespējama bezmaksas piegāde tajā pašā dienā.</p>
            </div>

            {/* Quality Materials */}
            <div className="flex flex-col items-center text-center text-white">
              <div className="bg-green-400 text-gray-900 p-3 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Augsta Kvalitāte</h3>
              <p className="text-gray-300">Piedāvājam tikai pārbaudītu ražotāju materiālus ar 5 gadu garantiju.</p>
            </div>

            {/* Easy Installation */}
            <div className="flex flex-col items-center text-center text-white">
              <div className="bg-green-400 text-gray-900 p-3 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Palīgs Īsziņas Attālumā</h3>
              <p className="text-gray-300">Kautkas nesanāk? Raksti WhatsApp, mūsu speciālisti palīdzēs! </p>
            </div>
          </div>
        </div>
      </div>

      <Reviews />

      {/* Quick Quote Form Section */}
      <div className="relative" id="contactform">
        <div className="container mx-auto px-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl p-6 md:p-6 md:fixed md:right-4 md:top-1/2 md:-translate-y-1/2 md:w-[380px] md:transform md:translate-x-0 md:z-50">
            <div className="text-center mb-4">
              <h2 className="text-2xl md:text-2xl font-bold text-white mb-2">PIERAKSTIES</h2>
              <div className="w-12 h-1 bg-green-400 mx-auto mb-2"></div>
              <Progress percent={30} showInfo={false} strokeColor="#4ade80" />
              <p className="text-gray-400 text-sm mt-1">Atbildam 60 minūšu laikā!</p>
            </div>

            <Form
              form={form}
              name="quick_quote"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              layout="vertical"
              requiredMark={false}
              onFieldsChange={handleFormChange}
              className="space-y-2"
            >
              {alertInfo.visible && (
                <Form.Item>
                  <Alert
                    message={alertInfo.message}
                    type={alertInfo.type}
                    showIcon
                    closable
                    onClose={() => setAlertInfo({ ...alertInfo, visible: false })}
                    className="mb-4"
                  />
                </Form.Item>
              )}

              <Form.Item
                name="name"
                label={<span className="text-white font-medium text-base md:text-sm">Vārds</span>}
                rules={[{ required: true, message: 'Lūdzu, ievadiet savu vārdu!' }]}
                className="mb-2"
              >
                <Input
                  prefix={<UserOutlined className="text-gray-400" />}
                  placeholder="Jūsu vārds"
                  className={`${inputStyle} pointer-events-auto h-10 md:h-8 text-base md:text-sm`}
                  size="large"
                  disabled={isSending}
                />
              </Form.Item>

              <Form.Item
                name="phone"
                label={<span className="text-white font-medium text-base md:text-sm">Telefona Nr.</span>}
                rules={[
                  { required: true, message: 'Lūdzu, ievadiet savu tālruņa numuru!' },
                  { len: 8, message: 'Telefona numuram jābūt 8 cipariem!' },
                  { pattern: /^\d+$/, message: 'Lūdzu, ievadiet tikai ciparus!' }
                ]}
                className="mb-2"
              >
                <Input
                  prefix={<PhoneOutlined className="text-gray-400" />}
                  placeholder="2XXXXXXX"
                  className={`${inputStyle} pointer-events-auto h-10 md:h-8 text-base md:text-sm`}
                  size="large"
                  disabled={isSending}
                />
              </Form.Item>

              <Form.Item
                name="email"
                label={<span className="text-white font-medium text-base md:text-sm">E-pasts (ieteicams)</span>}
                rules={[
                  { type: 'email', message: 'Lūdzu, ievadiet derīgu e-pasta adresi!' }
                ]}
                className="mb-2"
              >
                <Input
                  prefix={<MailOutlined className="text-gray-400" />}
                  placeholder="Jūsu e-pasts"
                  className={`${inputStyle} pointer-events-auto h-10 md:h-8 text-base md:text-sm`}
                  size="large"
                  disabled={isSending}
                />
              </Form.Item>

              <Form.Item
                name="service"
                label={<span className="text-white font-medium text-base md:text-sm">Kas jāaplīmē?</span>}
                rules={[{ required: true, message: 'Lūdzu, izvēlieties pakalpojumu!' }]}
                className="mb-2"
              >
                <Select
                  placeholder="Izvēlieties pakalpojumu"
                  className={`${inputStyle} pointer-events-auto h-10 md:h-8 text-base md:text-sm`}
                  size="large"
                  disabled={isSending}
                  options={[
                    { value: 'car', label: 'Auto / Transportlīdzeklis' },
                    { value: 'home', label: 'Mājas / Dzīvokļa logi' },
                    { value: 'commercial', label: 'Komerciālā ēka' },
                    { value: 'other', label: 'Cits' }
                  ]}
                />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.service !== currentValues.service}
              >
                {({ getFieldValue }) =>
                  getFieldValue('service') === 'other' ? (
                    <Form.Item
                      name="otherService"
                      rules={[{ required: true, message: 'Lūdzu, norādiet, ko vēlaties aplīmēt!' }]}
                      className="mb-2"
                    >
                      <Input
                        prefix={<MessageOutlined className="text-gray-400" />}
                        placeholder="Aprakstiet, ko vēlaties aplīmēt"
                        className={`${inputStyle} pointer-events-auto h-10 md:h-8 text-base md:text-sm`}
                        size="large"
                        disabled={isSending}
                      />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>

              <Form.Item className="text-center mt-4 mb-2">
                <Button
                  type="default"
                  htmlType="submit"
                  size="large"
                  loading={isSending}
                  disabled={isSending}
                  className="w-full md:w-auto px-6 py-4 h-auto text-base md:text-sm font-semibold rounded-lg tracking-wider 
                           bg-gradient-to-r from-green-400 to-green-500 text-gray-900 border-none
                           hover:from-green-500 hover:to-green-600 hover:shadow-lg hover:shadow-green-500/20
                           active:from-green-600 active:to-green-700
                           transition-all duration-300 ease-in-out
                           disabled:opacity-50 disabled:from-gray-600 disabled:to-gray-700 disabled:text-white 
                           disabled:hover:shadow-none disabled:cursor-not-allowed
                           pointer-events-auto relative overflow-hidden group"
                  style={{ height: 'auto' }}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {isSending ? (
                      <>
                        <span className="animate-spin">⟳</span>
                        SŪTA...
                      </>
                    ) : (
                      <>
                        SAŅEMT PIEDĀVĀJUMU
                        <svg 
                          className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 
                                transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </Button>
              </Form.Item>
            </Form>

            <div className="text-center mt-4 border-t border-gray-700 pt-4">
              <p className="text-gray-400 text-base md:text-sm mb-2">Nevēlaties aizpildīt formu?</p>
              <a
                href="https://wa.me/37126001205"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-400 text-gray-900 font-medium rounded-md hover:bg-green-500 transition-colors pointer-events-auto w-full md:w-auto justify-center text-base md:text-sm"
              >
                <WhatsAppOutlined />
                Atvērt WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Section;